/**
 * ReportComplaint.jsx: BM-H3370 (1 Oct 2026). The Complaint Notification Form.
 *
 * Complaint Management Policy (POL-18 v1.2) §6.1 to §6.4 and §15: every
 * complaint, written or verbal, is reported to the Complaints Manager, even if
 * it seems minor. A verbal complaint needs an attendance note (§6.2). Any
 * member of staff can report. Server: POST /api/compliance/complaints.
 */
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { MessageSquareWarning } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const id = sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return id ? { "X-Bonsai-User-Id": id } : {};
}

const CHANNELS = [
  ["letter", "Letter"], ["email", "Email"], ["phone", "Phone call"], ["in_person", "In person"],
  ["social_media", "Social media"], ["other", "Other"],
];
const INPUT = "w-full px-2 py-1.5 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-100";

export default function ReportComplaint() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    matter_ref: params.get("matter") || "", complainant_name: "", complainant_is_client: true,
    received_at: "", channel: "", summary: "", attendance_note: "", documents_link: "",
    data_protection: false, communication_needs: "", fee_earner: "",
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const verbal = form.channel === "phone" || form.channel === "in_person";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = { ...form, received_at: form.received_at ? new Date(form.received_at).toISOString() : undefined };
      const r = await axios.post(`${API}/compliance/complaints`, body, { headers: authHeaders() });
      setDone(r.data);
    } catch (err) {
      setError(errorMessage(err, "The complaint could not be reported"));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="p-6 max-w-2xl space-y-2" data-testid="complaint-reported">
        <h1 className="text-xl font-semibold text-slate-100">Complaint reported</h1>
        <p className="text-sm text-slate-300">
          Reference <span className="font-mono">{done.reference}</span>. The Complaints Manager has it and must
          acknowledge it by {new Date(done.acknowledge_by).toLocaleString("en-GB")}. Avoid direct contact with the
          complainant where you can; if you cannot, tell them it has gone to the Complaints Manager (policy §6.4).
        </p>
        {done.manager_notice?.status === "error" && (
          // BM-H3509: say so when the Complaints Manager could not be emailed.
          <p className="text-sm text-amber-300" data-testid="complaint-notice-failed">
            The Complaints Manager could not be emailed ({done.manager_notice.error}). Tell them about {done.reference}
            {" "}yourself today, because the 72 hour clock has started.
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="p-6 max-w-2xl space-y-4" data-testid="complaint-form">
      <header className="flex items-center gap-2">
        <MessageSquareWarning size={20} className="text-amber-300" />
        <h1 className="text-xl font-semibold text-slate-100">Report a complaint</h1>
      </header>
      <p className="text-sm text-slate-400">
        Report every complaint, even one you think is minor or have already resolved informally (Complaint
        Management Policy §6.4). If you are not sure it is a complaint, report it and the Complaints Manager will
        decide.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-300 space-y-1">Complainant<input id="c-name" className={INPUT}
          value={form.complainant_name} onChange={set("complainant_name")} /></label>
        <label className="text-xs text-slate-300 space-y-1">Matter reference (if any)<input id="c-matter" className={INPUT}
          value={form.matter_ref} onChange={set("matter_ref")} /></label>
        <label className="text-xs text-slate-300 space-y-1">How was it made?
          <select id="c-channel" className={INPUT} value={form.channel} onChange={set("channel")}>
            <option value="">Choose…</option>
            {CHANNELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></label>
        <label className="text-xs text-slate-300 space-y-1">When was it received?<input id="c-received" type="datetime-local"
          className={INPUT} value={form.received_at} onChange={set("received_at")} /></label>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" id="c-client" checked={form.complainant_is_client} onChange={set("complainant_is_client")} />
        The complainant is our client
      </label>
      <label className="text-xs text-slate-300 space-y-1 block">What is the complaint about?
        <textarea id="c-summary" className={INPUT} rows={4} value={form.summary} onChange={set("summary")} /></label>
      {verbal ? (
        <label className="text-xs text-slate-300 space-y-1 block" data-testid="c-attendance">
          Attendance note of the conversation (required for a verbal complaint, §6.2)
          <textarea id="c-note" className={INPUT} rows={4} value={form.attendance_note} onChange={set("attendance_note")} />
        </label>
      ) : null}
      <label className="text-xs text-slate-300 space-y-1 block">Link to the complaint and documents in SharePoint (if any)
        <input id="c-docs" className={INPUT} value={form.documents_link} onChange={set("documents_link")} /></label>
      <label className="text-xs text-slate-300 space-y-1 block">Any communication needs, such as hearing difficulties or language (§11)
        <input id="c-needs" className={INPUT} value={form.communication_needs} onChange={set("communication_needs")} /></label>
      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" id="c-dp" checked={form.data_protection} onChange={set("data_protection")} />
        It is about how we used or protected personal data
      </label>
      {error ? <p className="text-sm text-red-300" data-testid="complaint-error">{error}</p> : null}
      <button type="submit" disabled={busy} data-testid="complaint-submit"
        className="px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-500 text-sm text-white disabled:opacity-60">
        {busy ? "Reporting…" : "Report to the Complaints Manager"}
      </button>
    </form>
  );
}
